import {
  bankInitials,
  fundingBankLast4,
  fundingChipAccountKind,
  fundingChipLabel,
} from '@wavvest/wealth-ui/parts';
import type {
  FundingChip,
  FundingChipAccountKind,
  FundingChipStatus,
} from '@wavvest/wealth-ui/parts';
import { formatAccountType } from './account-type-format';

export type BankLinkCurrentLink = {
  plaid_id?: string | null;
  relationship_id: string;
  relationship_name: string;
  state: string;
  masked_number?: string;
};

export type BankLinkReuseSource = {
  plaid_id: string;
  source_account_id: string;
  source_account_name: string;
  source_relationship_name: string;
  state: string;
};

export type BankLinkWallet = {
  account_id: string;
  account_name: string;
  goal: string;
  current_link?: BankLinkCurrentLink | null;
  slot_available: boolean;
};

export type BankLinkOptionsResponse = {
  wallets: BankLinkWallet[];
  funding_account_ids?: string[];
  reuse_sources: BankLinkReuseSource[];
  pending_sources?: BankLinkReuseSource[];
};

export type AssignBankRelationshipResponse = {
  account_id: string;
  plaid_id: string;
  relationship_id: string;
  relationship_name: string;
  state: string;
  method: 'created' | 'reused' | 'already_linked';
};

/** What unlinking a bank does to the user's Apex accounts. Answered by fo-apex. */
export type BankUnlinkCheckResponse = {
  /** Apex accounts that lose this bank as a funding bank when it is unlinked. */
  affected_accounts: { account_id: string; account_name: string }[];
  /** Deposits or withdrawals through this bank that have not finished. */
  pending_transfers: {
    account_id: string;
    account_name: string;
    kind: 'deposit' | 'withdrawal';
    transfer_id: string;
  }[];
  can_unlink: boolean;
};

export const ACTIVE_FUNDING_BANK_STATES = new Set(['APPROVED', 'PENDING']);

export const FUNDING_BANK_SLOT_EMPTIED_MESSAGE =
  'The previous bank was removed, but we could not connect the new one. Please try connecting again.';

export const isFundingBankSlotEmptiedError = (errorMessage: string): boolean =>
  errorMessage.includes('previous bank was removed');

export const walletHasActiveFundingBank = (wallet: BankLinkWallet): boolean =>
  ACTIVE_FUNDING_BANK_STATES.has(wallet.current_link?.state ?? '');

export const walletNeedsBankLink = (wallet: BankLinkWallet): boolean => wallet.slot_available;

export const BANK_LINK_PICKER_LOOKUP_TIMEOUT_MS = 4000;

export const walletsNeedBankPicker = (options: BankLinkOptionsResponse): boolean =>
  options.wallets.some(walletNeedsBankLink);

export const BANK_LINK_PICKER_PATH = '/home/bank-link';

export const bankLinkPickerPath = (accountId?: string): string =>
  accountId
    ? `${BANK_LINK_PICKER_PATH}?accountId=${encodeURIComponent(accountId)}`
    : BANK_LINK_PICKER_PATH;

export const fundingAccountIdForListAccount = (account: {
  id: string;
  institutionType?: string | null;
  custodian_code?: string | null;
  custodian_account_id?: string | null;
}): string | undefined => {
  if (account.institutionType === 'apex') {
    return account.id;
  }
  if (account.custodian_code === 'APEX' && account.custodian_account_id) {
    return account.custodian_account_id;
  }
  return undefined;
};

export const isWealthOverviewRoute = (pathname: string): boolean =>
  /^\/home\/wealth\/?$/.test(pathname);

export const lookupBankPickerRedirect = async (
  fetchOptions: () => Promise<BankLinkOptionsResponse>,
  timeoutMs: number = BANK_LINK_PICKER_LOOKUP_TIMEOUT_MS,
): Promise<boolean> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    const options = await Promise.race([
      fetchOptions(),
      new Promise<BankLinkOptionsResponse>((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new Error('Timed out loading bank link options'));
        }, timeoutMs);
      }),
    ]);
    return walletsNeedBankPicker(options);
  } catch (error) {
    console.error('Failed to load bank link options after Plaid link', error);
    return false;
  } finally {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }
  }
};

export const findReuseSource = (
  reuseSources: readonly BankLinkReuseSource[],
  plaidId: string,
): BankLinkReuseSource | undefined => reuseSources.find((source) => source.plaid_id === plaidId);

export type FundingBankAccount = {
  account_id?: string;
  plaid_account_id?: string | null;
  name?: string | null;
  mask?: string | null;
  account_type?: string | null;
  institution_name?: string | null;
  institution_logo?: string | null;
};

export const formatFundingBankLabel = (
  account: FundingBankAccount,
  options: { includeMask?: boolean } = {},
): string => {
  const includeMask = options.includeMask !== false;
  const institution = account.institution_name?.trim() || '';
  const accountName = account.name?.trim() || '';
  const typeLabel = formatAccountType(account.account_type);
  const mask = account.mask?.trim() || '';

  const parts: string[] = [];
  if (institution) {
    parts.push(institution);
    if (typeLabel && typeLabel.toLowerCase() !== institution.toLowerCase()) {
      parts.push(typeLabel);
    } else if (
      accountName &&
      !accountName.toLowerCase().includes(institution.toLowerCase()) &&
      accountName.toLowerCase() !== typeLabel.toLowerCase()
    ) {
      parts.push(accountName);
    }
  } else if (accountName) {
    parts.push(accountName);
    if (typeLabel && !accountName.toLowerCase().includes(typeLabel.toLowerCase())) {
      parts.push(typeLabel);
    }
  } else {
    parts.push('Linked bank');
  }

  if (includeMask && mask) {
    parts.push(`**** ${mask}`);
  }

  return parts.join(' · ');
};

export const findFundingBankAccount = <TAccount extends FundingBankAccount>(
  accounts: readonly TAccount[],
  plaidId?: string | null,
): TAccount | undefined => {
  const normalizedPlaidId = plaidId?.trim();
  if (!normalizedPlaidId) {
    return undefined;
  }

  return accounts.find(
    (account) =>
      account.account_id === normalizedPlaidId || account.plaid_account_id === normalizedPlaidId,
  );
};

export const connectedFundingBankLabel = (
  wallet: BankLinkWallet,
  accounts: readonly FundingBankAccount[],
): string => {
  const linkedAccount = findFundingBankAccount(accounts, wallet.current_link?.plaid_id);
  if (linkedAccount) {
    return formatFundingBankLabel(linkedAccount, { includeMask: false });
  }
  const maskedNumber = wallet.current_link?.masked_number?.trim();
  return maskedNumber ? `**** ${maskedNumber}` : '';
};

export { fundingBankLast4, fundingChipAccountKind, fundingChipLabel };
export type { FundingChip, FundingChipAccountKind, FundingChipStatus };

export const fundingBankInitials = (bankName?: string): string => bankInitials(bankName);

/** Plaid sends institution logos as bare base64 PNGs; URLs pass through. */
export const fundingBankLogoSrc = (logo?: string | null): string | undefined => {
  const value = logo?.trim();
  if (!value) {
    return undefined;
  }
  if (value.startsWith('data:') || value.startsWith('http') || value.startsWith('/')) {
    return value;
  }
  return `data:image/png;base64,${value}`;
};

export const fundingBankRowSubtitle = (account: FundingBankAccount): string =>
  fundingChipLabel({
    accountId: account.account_id || 'bank',
    status: 'connected',
    bankName: account.institution_name?.trim() || undefined,
    accountType: formatAccountType(account.account_type) || undefined,
    last4: fundingBankLast4(account.mask),
  });

export const fundingBankChangedMessage = (account: FundingBankAccount): string => {
  const bankName = account.institution_name?.trim() || 'Linked bank';
  const typeLabel = formatAccountType(account.account_type);
  const last4 = fundingBankLast4(account.mask);
  return `Funding bank changed to ${[bankName, typeLabel, last4 ? `••${last4}` : '']
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ')}`;
};

const fundingChipDetails = (chip: FundingChip): string =>
  [chip.bankName, chip.accountType, chip.last4 ? `••${chip.last4}` : '']
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');

export const fundingChipTooltip = (chip: FundingChip): string => {
  if (chip.status === 'not_set') {
    return 'Add a funding bank';
  }
  if (chip.status === 'disconnected') {
    return chip.bankName?.trim()
      ? `Connection lost. Reconnect ${chip.bankName.trim()}.`
      : 'Connection lost. Reconnect this bank.';
  }
  const details = fundingChipDetails(chip);
  if (chip.status === 'pending') {
    return details ? `Apex is still approving ${details}` : 'Apex is still approving this bank';
  }
  return details ? `Funded from ${details}` : 'Funded from a linked bank';
};

export const fundingChipForWallet = (
  wallet: BankLinkWallet,
  accounts: readonly FundingBankAccount[],
): FundingChip => {
  if (walletNeedsBankLink(wallet)) {
    return { accountId: wallet.account_id, status: 'not_set' };
  }

  const linkedAccount = findFundingBankAccount(accounts, wallet.current_link?.plaid_id);
  const last4 =
    fundingBankLast4(linkedAccount?.mask) ?? fundingBankLast4(wallet.current_link?.masked_number);
  const accountType = formatAccountType(linkedAccount?.account_type) || undefined;
  const bankName = linkedAccount?.institution_name?.trim() || undefined;

  return {
    accountId: wallet.account_id,
    plaidId: wallet.current_link?.plaid_id || undefined,
    status: wallet.current_link?.state === 'PENDING' ? 'pending' : 'connected',
    bankName,
    bankLogo: linkedAccount?.institution_logo,
    accountType,
    last4,
  };
};

/** A bank an attach-all wallet can move money with, as the transfer modal lists it. */
export type TransferBankChoice = {
  plaidId: string;
  name: string;
  detail: string;
  logo?: string | null;
  /** Only Apex-approved banks can be used; pending ones are listed but disabled. */
  approved: boolean;
};

export const isExternalBankListAccount = (account: {
  institutionType?: string | null;
  custodian_code?: string | null;
  type?: string | null;
}): boolean => {
  if (account.institutionType !== 'plaid' || account.custodian_code === 'APEX') {
    return false;
  }
  const type = (account.type ?? '').trim().toLowerCase();
  if (!type || type.startsWith('investment/') || type === 'taxable_brokerage') {
    return false;
  }
  return (
    type.startsWith('depository/') ||
    type === 'checking' ||
    type === 'savings' ||
    type === 'saving' ||
    type === 'money_market' ||
    type === 'cd' ||
    type === 'cash_management' ||
    type === 'hsa'
  );
};

export { formatExternalBankAccountTitle } from '@wavvest/shared-utils';

export const formatExternalBankAccountSubtitle = (account: {
  type?: string | null;
  account_type?: string | null;
  mask?: string | null;
}): string => {
  const accountType = formatAccountType(account.account_type ?? account.type) || undefined;
  const last4 = fundingBankLast4(account.mask);
  if (!accountType && !last4) {
    return '';
  }
  return fundingChipLabel({
    accountId: '',
    status: 'connected',
    accountType,
    last4,
  });
};
